import { FlightRouteInfo, FullBaggageResponse, JsonsData, MiniRuleData } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { getFlightLogoUrl } from '@/src/utils/generalUtils/airlineImage'
import { transformSRPLayoverData } from '@/src/utils/srp/flightTransformarSrp'
import { Box } from '@mui/material'
import { FC } from 'react'

import AircraftAmenitiesWrapper from '@/src/components/common/srp/aircraftAmenities'

import LayoverBlockWrapper from '../../itinerary/itineraryHeader/flightDetails/layoverBlock'
import SegmentBlockWrapper from '../../itinerary/itineraryHeader/flightDetails/segmentBlock'
import FareRulesWrapper from '../fareRules'

interface FlightDetailsProps {
    targetSectors?: FlightRouteInfo[]
    jsons: JsonsData
    layOverDurationMap?: FlightRouteInfo['layoverDurationMap']
    baggageData: FullBaggageResponse | undefined
    baggageLoading: boolean
    baggageError: string | null
    minirule: MiniRuleData | undefined
    miniRuleLoading: boolean
    miniRuleError: string | null
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    tCommon: TranslationFunction<'common'>
    formatDate: (date: string, format?: string) => string
    showBaggage?: boolean
    retryCount?: number
    handleRetryBaggage?: () => void
    maxRetries?: number
    transitVisaSegments?: string[]
}

const FlightDetails: FC<FlightDetailsProps> = ({
    targetSectors,
    jsons,
    baggageData,
    baggageLoading,
    baggageError,
    minirule,
    miniRuleLoading,
    miniRuleError,
    tFlightDetails,
    tCommon,
    formatDate,
    showBaggage,
    retryCount = 0,
    handleRetryBaggage,
    maxRetries = 2,
    transitVisaSegments
}) => {
    return (
        <Box sx={{ mt: 1 }}>
            {targetSectors?.map((sector, sectorIndex) => (
                <Box
                    key={sectorIndex}
                    sx={{
                        p: 2,
                        bgcolor: 'neutral.white',
                        border: 'solid 1px',
                        borderColor: 'borders.default',
                        borderRadius: 2,
                        mb: 2
                    }}
                >
                    {sector?.flights?.segments.map((segment, segmentIndex) => {
                        const departureAirport = jsons?.ap?.[segment.departure.airportCode]
                        const arrivalAirport = jsons?.ap?.[segment.arrival.airportCode]
                        const segmentKey = `${segment.departure.airportCode}_${segment.arrival.airportCode}_${formatDate(
                            new Date(segment.departure.timestamp).toISOString(),
                            'YYYY-MM-DD'
                        )}_${segment.departure.time}`

                        return (
                            <Box key={`${sectorIndex}-${segmentIndex}`}>
                                <SegmentBlockWrapper
                                    segmentData={{
                                        arrivalCode: segment.arrival.airportCode,
                                        arrivalTime: segment.arrival.time,
                                        arrivalTerminal: segment.arrival.terminal,
                                        arrivalAirport: arrivalAirport?.n || '',
                                        departureCode: segment.departure.airportCode,
                                        departureTime: segment.departure.time,
                                        arrivalDate: segment.arrival.date,
                                        departureDate: segment.departure.date,
                                        airline: segment.flightName,
                                        airlineLogo: getFlightLogoUrl(segment.flightNumber?.split('-')[0] || ''),
                                        duration: {
                                            hours: segment.durationTime.hh,
                                            mins: segment.durationTime.mm
                                        },
                                        segmentKey: segmentKey,
                                        route: `${segment.departure.airportCode} → ${segment.arrival.airportCode}`,
                                        departureAirport: departureAirport?.n || '',
                                        departureTerminal: segment.departure.terminal,
                                        airlineName: segment.flightName,
                                        flightNumber: segment.flightNumber,
                                        cabinType: segment.flightClass,
                                        operatingAirlineName: jsons?.airline_names?.[segment.oa],
                                        srpFlightDetails: true
                                    }}
                                    showBaggageInfo={false}
                                />
                                <AircraftAmenitiesWrapper
                                    airlineCode={segment.ac}
                                    aircraftCode={segment.act}
                                    flightClass={segment.flightClass}
                                    showKnowMore
                                />
                                {/* Add layover between segments (but not after the last segment) */}
                                {segmentIndex < sector?.flights?.segments.length - 1 && (
                                    <LayoverBlockWrapper
                                        layoverData={transformSRPLayoverData(
                                            sector?.flights?.segments[segmentIndex],
                                            sector?.flights?.segments[segmentIndex + 1],
                                            sector.layoverDurationMap[segmentIndex] || 0,
                                            jsons
                                        )}
                                        transitVisa={transitVisaSegments?.includes(
                                            `${segment.departure.airportCode}_${segment.arrival.airportCode}`
                                        )}
                                    />
                                )}
                            </Box>
                        )
                    })}
                </Box>
            ))}
            {/* Move FareRulesWrapper outside the map to render once */}
            {targetSectors && targetSectors.length > 0 && (
                <FareRulesWrapper
                    baggageData={baggageData}
                    baggageLoading={baggageLoading}
                    baggageError={baggageError}
                    miniruleData={minirule}
                    miniRuleLoading={miniRuleLoading}
                    miniRuleError={miniRuleError}
                    targetSectors={targetSectors}
                    formatDate={formatDate}
                    tCommon={tCommon}
                    showBaggage={showBaggage}
                    retryCount={retryCount}
                    handleRetryBaggage={handleRetryBaggage}
                    maxRetries={maxRetries}
                />
            )}
        </Box>
    )
}

export default FlightDetails
